import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Wraps the browser SpeechRecognition API for a voice-to-text mic button.
 * - Continuous listening with interim (live) results streamed to `onTranscript`.
 * - Cleans up on unmount and on stop.
 * - `supported = false` on browsers without the API (Firefox, older mobiles).
 *
 * onTranscript({ text, isFinal }) fires as the user speaks. Consumer decides
 * whether to append or replace the input field.
 */
export function useSpeechRecognition({ onTranscript, lang = "en-US" } = {}) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const finalRef = useRef("");

  const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const supported = !!SR;

  const stop = useCallback(() => {
    if (recRef.current) {
      try { recRef.current.stop(); } catch (_) { /* noop */ }
    }
  }, []);

  const start = useCallback(() => {
    if (!SR) { setError("Voice input isn't supported in this browser. Try Chrome or Safari."); return; }
    setError(null);
    finalRef.current = "";
    const rec = new SR();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      let interim = "";
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      if (final) finalRef.current += final;
      const text = (finalRef.current + interim).trim();
      onTranscript?.({ text, isFinal: !!final });
    };
    rec.onerror = (e) => {
      // Common: "no-speech", "audio-capture", "not-allowed"
      const msg = e.error === "not-allowed"
        ? "Microphone access was blocked. Enable it in your browser settings."
        : e.error === "no-speech"
        ? null // silent — user paused
        : `Voice input error: ${e.error}`;
      if (msg) setError(msg);
    };
    rec.onend = () => { setListening(false); recRef.current = null; };

    try {
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch (err) {
      setError("Couldn't start voice input. Please try again.");
    }
  }, [SR, lang, onTranscript]);

  const toggle = useCallback(() => (listening ? stop() : start()), [listening, start, stop]);

  useEffect(() => () => stop(), [stop]);

  return { listening, supported, error, start, stop, toggle };
}
